// The CER catalog: everything about this template that the shared wizard reads.
// Its counterpart on the backend is the `CER` entry in services/cer/templates.js,
// and the two are keyed by the same `id`.
import { GENERAL_SECTION } from "../fields";

// Catalog of CER sections the user can generate and assign documents to.
//
// `key` is what gets persisted in the config and sent to the backend, which
// translates it to the adapter's numbered contract keys (see
// backend/src/services/cer/sections.js). Names rather than numbers, because the
// numbering changes between CER templates.
const SECTION_CATALOG = [
    { key: "scope", label: "Scope of the Clinical Evaluation" },
    { key: "device", label: "Device Overview" },
    { key: "sota", label: "Clinical Background, Current Knowledge and State-of-the-Art" },
    { key: "non_clinical", label: "Non-Clinical and/or Pre-Clinical Data" },
    { key: "clinical", label: "Clinical Investigations" },
    { key: "literature", label: "Published Scientific Literature on DUE" },
];

// The one section whose assignment order is load-bearing: insertion order in the
// adapter's "DUE" list drives the article numbering in §6.2.1. So it gets an
// explicit ordering control instead of a priority ranking.
const ORDERED_SECTION_KEY = "literature";

// Sections scoped by the project's screened literature rather than by source
// documents, and which article channel each one reads. The values are
// `ProjectArticle.type`: "slr" is DUE, "sota" is SoTA. Both pickers list only
// L2-included articles.
//
// Section 4 is the odd one: the adapter gives it no per-document scoping at all
// (it reads every document in the run), so its selection decides which articles
// are *uploaded*, not what §4 may look at. See CAPTIS_SOTA_SECTION_4.md §2.3.
const SECTION_ARTICLE_TYPE = {
    sota: "sota",
    literature: "slr",
};

// Sections that take no source documents. Their scope comes from the article
// picker instead (`sota`, `literature`) or from their panel fields alone.
//
// A selected section still gets an entry in `section_documents` or
// `section_articles` — an empty array — so the selection survives a reload:
// `fromConfig` derives the selected set from those maps' keys. The backend drops
// empty keys when building the run.
const SECTIONS_WITHOUT_DOCUMENTS = new Set(["sota", "literature"]);

// The wizard is section-first: pick what to generate, then fill in only what
// those sections need.
const STEPS = [
    { key: "sections", title: "Select Sections" },
    { key: "details", title: "Configure" },
];

// Sub-headings inside a panel, keyed `<section>:<block>`. A field with no
// `block` renders with no heading, at the top of its panel.
const BLOCK_TITLES = {
    "general:core": "Device and regulatory facts",
    "general:equivalence": "Equivalent device",
    "sota:required": "Required",
    "sota:context": "Device context",
    "sota:scope": "Subsection scope",
    "sota:stats": "Intro statistics",
};

// Blocks that only apply to some sections — the block is hidden unless at least
// one of them is selected. A block absent from here is always shown.
const BLOCK_SECTIONS = {
    // The engine runs a hidden `core` pass shared by Sections 2, 3 and 6, so
    // these are collected once rather than repeated in three panels
    // (SECTION_FIELD_MAP §3).
    "general:core": ["scope", "device", "non_clinical", "clinical"],
    "general:equivalence": ["non_clinical", "clinical", "literature"],
};

const BLOCK_DESCRIPTIONS = {
    "general:core": "Shared by the Scope, Device Overview and other data sections.",
    "sota:stats":
        "Rendered verbatim into the intro paragraph. An unset value renders as a visible [field_name] placeholder.",
};

// Single source of truth for the wizard form.
//
// `section` is either GENERAL_SECTION or a SECTION_CATALOG key — it decides
// which panel the field appears in, and therefore whether the user is asked for
// it at all. `block` optionally groups fields under a sub-heading.
//
// `name` is the *engine's* field name, not a label — it is what lands in the
// adapter's `[metadata]` table, and several read the opposite way round from the
// label above them (`launch_date`, `initial_cer`, `already_launched`,
// `clinical_data_available`, `total_units`). Renaming one here renames it on the
// wire; the names were reconciled against the engine's own section/field map
// (ai-cer-v2 `documents/SECTION_FIELD_MAP.md`), so do not "tidy" them.
//
// The `[sota]` fields are the exception: Section 4 is configured by a separate
// table whose keys collide with `[metadata]` ones of the same name, so those
// entries carry `group: "sota"` plus an explicit `wireName`, and `name` is only
// the (unique) key in the flat form state.
//
// `type` drives the renderer (see the FieldControl switch in field-list.js).
const FIELDS = [
    // Every field the backend requires for a run lives here, so the always-
    // visible panel is on its own enough to satisfy validateConfig no matter
    // which sections the user picked.
    {
        section: GENERAL_SECTION,
        name: "device_names",
        label: "Device Name(s)",
        type: "string_list",
        description:
            "Complete name of all subject devices in scope. Injected into the prompt of every extraction.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "manufacturer",
        label: "Manufacturer",
        type: "text",
        description: "Name of the manufacturer of the subject device",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "intended_purpose",
        label: "Intended Purpose",
        type: "long_text",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "disease_condition",
        label: "Disease or Condition",
        type: "text",
    },
    {
        section: GENERAL_SECTION,
        name: "device_family_type",
        label: "Device Family Type",
        type: "text",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "device_family_category",
        label: "Device Family Category",
        type: "select",
        options: ["Device", "Software", "Device + Software", "Drug-Device", "Other"],
    },
    {
        section: GENERAL_SECTION,
        name: "device_family_list",
        label: "Device Family List",
        type: "textarea",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "date_format",
        label: "Date Format",
        type: "select",
        // Every date the wizard collects is serialised into this format at the
        // request boundary, so the option list has to be one the engine parses —
        // a free-text "Custom" would render verbatim into the CER.
        options: ["dd-mm-yyyy", "dd/mm/yyyy", "mm/dd/yyyy", "DD Month YYYY"],
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "launch_date",
        label: "Device Launch Date",
        type: "date",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "launch_country",
        label: "Country of Device Launch",
        type: "country",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "ce_mark_date_mdr",
        label: "MDR CE Mark Date",
        type: "date",
        description: "Date the device received its CE mark under the MDR.",
    },
    {
        section: GENERAL_SECTION,
        name: "development_stage",
        label: "Stage of Development",
        type: "select",
        options: [
            "Verification and Validation",
            "Preclinical Testing",
            "Clinical Evaluation",
            "Commercialization",
            "Other",
        ],
        description: "Only read for one section, but required for every run.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "initial_cer",
        label: "Initial CER for New Product",
        type: "boolean",
        description: "Selects the design scenario. Required for every run.",
        required: true,
    },
    {
        section: GENERAL_SECTION,
        name: "evaluation_start_date",
        label: "Evaluation Period Start",
        type: "date",
    },
    {
        section: GENERAL_SECTION,
        name: "evaluation_end_date",
        label: "Evaluation Period End",
        type: "date",
    },

    // general — the engine's shared `core` pass (Sections 2, 3, 6)
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "technical_file_number",
        label: "Technical File Number",
        type: "text",
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "cep_document_number",
        label: "CEP Document Number",
        type: "text",
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "already_launched",
        label: "Device Already Launched",
        type: "boolean",
        description:
            "Gates the conditional logic below: when false the regulatory-lifecycle fields are extracted, when true they are marked not applicable.",
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "years_until_launch",
        label: "Years Until Launch",
        type: "number",
        precision: 0,
        min: 0,
        description:
            "Read only when Device Already Launched is No. Left empty, the sentence renders a data-not-available placeholder in its place.",
        requiredIf: { field: "already_launched", value: false },
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "estimated_launch_date",
        label: "Estimated Launch Date",
        type: "date",
        description: "Read only when Device Already Launched is No.",
        requiredIf: { field: "already_launched", value: false },
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "launch_date_eu",
        label: "Device Launch Date in EU",
        type: "date",
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "first_evaluation_report",
        label: "First Clinical Evaluation Report for Device",
        type: "boolean",
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "last_evaluation_date",
        label: "Date of Last Clinical Evaluation",
        type: "date",
    },
    {
        section: GENERAL_SECTION,
        block: "core",
        name: "last_evaluation_data_through",
        label: "Last Clinical Evaluation Included Data Until",
        type: "date",
    },

    // general — equivalence, read by 6.1.1, 6.1.2 and 6.2.1
    {
        section: GENERAL_SECTION,
        block: "equivalence",
        name: "equivalent_device_available",
        label: "Equivalent Device Available",
        type: "boolean",
        description:
            "With Clinical Data Available, selects the clinical scenario. Also gates whether the names below are read at all.",
    },
    {
        section: GENERAL_SECTION,
        block: "equivalence",
        name: "equivalent_device_names",
        label: "Equivalent Device Name(s)",
        type: "string_list",
        requiredIf: { field: "equivalent_device_available", value: true },
    },

    {
        section: "scope",
        name: "total_units",
        label: "Total Units of Device",
        type: "number",
        description: "Units sold in the reporting period.",
    },
    {
        section: "scope",
        name: "total_sales_units",
        label: "Overall Sales Units Since Launch",
        type: "number",
        description: "Cumulative units since launch.",
    },
    { section: "scope", name: "sales_start_date", label: "Sales Period Start", type: "date" },
    { section: "scope", name: "sales_end_date", label: "Sales Period End", type: "date" },

    {
        section: "device",
        name: "design_changes_in_period",
        label: "Design Changes Occurred in Current Review Period",
        type: "boolean",
        description: "With Initial CER, selects the design scenario: neither set means no_changes.",
    },
    // hidden for demo
    // {
    //     section: "device",
    //     name: "design_history_summary",
    //     label: "Summary of Design History",
    //     type: "long_text",
    //     description: "Leave blank to make the agent search the source documents instead.",
    // },
    {
        section: "device",
        name: "tentative_launch_year",
        label: "Tentative Launch Year",
        type: "number",
        description: "Used to compute years-on-market.",
    },
    {
        section: "device",
        name: "update_for_changes",
        label: "Update Frequency for Device Changes",
        type: "select",
        // The engine matches the lower-cased label, so the value and the display
        // text differ here. No "Custom": an unresolved placeholder renders
        // verbatim into the regulatory document.
        options: [
            { value: "annual", label: "Annual" },
            { value: "biannual", label: "Biannual" },
            { value: "5-year", label: "5-Year" },
        ],
    },

    // A separate `[sota]` table, not `[metadata]`. Section 4 is config-driven:
    // it takes no source documents, and these fields are its whole input.
    {
        section: "sota",
        block: "required",
        group: "sota",
        name: "sota_device_names",
        wireName: "device_names",
        label: "Device Name(s)",
        type: "string_list",
        description:
            "This section reads its own device names, not the ones above. At least one is required.",
        required: true,
    },
    {
        section: "sota",
        block: "required",
        group: "sota",
        name: "sota_intended_use",
        wireName: "intended_use",
        label: "Intended Use",
        type: "long_text",
        required: true,
    },
    {
        section: "sota",
        block: "required",
        group: "sota",
        name: "sota_device_type",
        wireName: "device_type",
        label: "Device Type",
        type: "text",
        required: true,
    },
    {
        section: "sota",
        block: "required",
        group: "sota",
        name: "sota_indications",
        wireName: "indications",
        label: "Indications",
        type: "long_text",
        required: true,
    },
    {
        section: "sota",
        block: "context",
        group: "sota",
        name: "sota_project_name",
        wireName: "project_name",
        label: "Project Name",
        type: "text",
        description:
            "Subject label in the rendered tables. Defaults to the client name when unset.",
    },
    {
        section: "sota",
        block: "context",
        group: "sota",
        name: "sota_manufacturer",
        wireName: "manufacturer",
        label: "Manufacturer",
        type: "text",
    },
    {
        section: "sota",
        block: "context",
        group: "sota",
        name: "sota_device_class",
        wireName: "device_class",
        label: "Device Class",
        type: "text",
    },
    {
        section: "sota",
        block: "context",
        group: "sota",
        name: "sota_device_description",
        wireName: "device_description",
        label: "Device Description",
        type: "long_text",
    },
    {
        section: "sota",
        block: "context",
        group: "sota",
        name: "sota_locale",
        wireName: "locale",
        label: "Locale",
        type: "select",
        // Validated by the engine — anything other than these two aborts the run.
        options: ["US", "UK"],
        description: "Spelling and language used throughout this section.",
    },
    {
        section: "sota",
        block: "scope",
        group: "sota",
        name: "sota_alternative_techniques",
        wireName: "alternative_techniques",
        label: "Alternative Techniques",
        type: "string_list",
        description:
            "Drives this subsection, under your exact spelling. Leave empty and it is suppressed entirely — there is no discovery fallback.",
    },
    {
        section: "sota",
        block: "scope",
        group: "sota",
        name: "sota_competitors",
        wireName: "competitors",
        label: "Competitors",
        type: "string_list",
        description:
            'Drives this subsection. Preferred form is "<device>: <manufacturer>"; a bare device name is tolerated.',
    },
    {
        section: "sota",
        block: "scope",
        group: "sota",
        name: "sota_clinical_benchmark_metrics",
        wireName: "clinical_benchmark_metrics",
        label: "Clinical Benchmark Metrics",
        type: "string_list",
        description: "Drives the benchmarks and acceptance criteria.",
    },
    {
        section: "sota",
        block: "scope",
        group: "sota",
        name: "sota_sota_status",
        wireName: "sota_status",
        label: "State-of-the-Art Status",
        type: "text",
        description: 'Prompt context, e.g. "current_standard".',
    },
    {
        section: "sota",
        block: "scope",
        group: "sota",
        name: "sota_is_ddc",
        wireName: "is_ddc",
        label: "Diagnostic Device Context",
        type: "boolean",
    },
    {
        section: "sota",
        block: "stats",
        group: "sota",
        name: "sota_cep_id",
        wireName: "cep_id",
        label: "CEP ID",
        type: "text",
    },
    {
        section: "sota",
        block: "stats",
        group: "sota",
        name: "sota_review_start_date",
        wireName: "review_start_date",
        label: "Search Window Start",
        type: "text",
        // Free text rendered verbatim, so it is deliberately not a DatePicker:
        // an ISO timestamp would land in the document as-is.
        placeholder: "01 Sep 2022",
    },
    {
        section: "sota",
        block: "stats",
        group: "sota",
        name: "sota_review_end_date",
        wireName: "review_end_date",
        label: "Search Window End",
        type: "text",
        placeholder: "31 Aug 2025",
    },
    {
        section: "sota",
        block: "stats",
        group: "sota",
        name: "sota_total_articles",
        wireName: "total_articles",
        label: "Total Articles Screened",
        type: "text",
    },
    {
        section: "sota",
        block: "stats",
        group: "sota",
        name: "sota_number_of_sota_articles",
        wireName: "number_of_sota_articles",
        label: "Articles Selected for Detailed Analysis",
        type: "text",
    },
    {
        section: "sota",
        block: "stats",
        group: "sota",
        name: "sota_previous_cer_number_of_sota_articles_included",
        wireName: "previous_cer_number_of_sota_articles_included",
        label: "Articles Carried Forward From the Previous CER",
        type: "text",
    },

    {
        section: "non_clinical",
        name: "biocompatibility_tests",
        label: "Biocompatibility Tests",
        type: "clinical_entry_list",
        description:
            "When provided this is the exhaustive list — the agent extracts metadata for exactly these tests instead of discovering them.",
    },
    {
        section: "non_clinical",
        name: "non_clinical_tests",
        label: "Non-Clinical Tests",
        type: "clinical_entry_list",
        description: "Same semantics as above: provide it and discovery is skipped.",
    },

    {
        section: "clinical",
        name: "clinical_data_available",
        label: "Clinical Data Held by Manufacturer Available",
        type: "boolean",
        description:
            "One of the two gates: study identification only runs for a clinical client (this, or at least one study title below).",
    },
    {
        section: "clinical",
        name: "clinical_study_titles",
        label: "Clinical Study Titles",
        type: "clinical_entry_list",
        description:
            "The studies that anchor extraction. When non-empty this is the exhaustive list instead of discovering studies.",
    },

    {
        section: "literature",
        name: "device_description",
        label: "Device Description",
        type: "long_text",
        description: "Part of the device-context block used when extracting the literature.",
    },
    {
        section: "literature",
        name: "literature_search_databases",
        label: "Literature Search Databases",
        type: "string_list",
        description: "Rendered in the intro paragraph. Shows as N/A when unset.",
    },
    {
        section: "literature",
        name: "literature_search_date_range",
        label: "Literature Search Date Range",
        type: "date_range",
        description: "Falls back to the evaluation period in General Info, then to N/A.",
    },
];

// UI-only fields, never dumped into the metadata bag: the three
// `device_family_*` fields feed the typed `device_family[]` array, and reaching
// `[metadata]` under these names is a 400.
const NON_METADATA_KEYS = new Set([
    "device_family_type",
    "device_family_category",
    "device_family_list",
]);

// Panel-level warnings for the cases where an under-filled section produces an
// empty or missing output rather than an error — each of these otherwise only
// surfaces as a wrong CER hours later.
//
// Owned by the template rather than by the panel: the keys read as generic
// ("clinical", "literature") and a second template reusing one would otherwise
// silently show CER copy.
const advisories = (sectionKey, values) => {
    const out = [];

    if (sectionKey === "clinical") {
        const titles = values.clinical_study_titles;
        if (values.clinical_data_available !== true && !(titles && titles.length)) {
            out.push({
                type: "warning",
                message: "This section will produce nothing as configured",
                description:
                    "Study identification only runs when clinical data is available, or at least one study title is listed. With neither, there is nothing to extract.",
            });
        }
    }

    if (sectionKey === "literature") {
        out.push({
            type: "info",
            message: "A literature search export is also required",
            description:
                "This section needs a RIS export of the literature search for its Reference column, which is supplied outside this wizard. Without it the section is generated but its references are left empty.",
        });
    }

    if (sectionKey === "sota") {
        const techniques = values.sota_alternative_techniques;
        if (!(techniques && techniques.filter((t) => t && t.trim()).length)) {
            out.push({
                type: "warning",
                message: "Alternative Techniques will be skipped",
                description:
                    "Alternative Techniques has no discovery fallback — leave it empty and the subsection is suppressed before any analysis runs.",
            });
        }
    }

    return out;
};

export default {
    id: "cer",
    steps: STEPS,
    sectionCatalog: SECTION_CATALOG,
    fields: FIELDS,
    blockTitles: BLOCK_TITLES,
    blockSections: BLOCK_SECTIONS,
    blockDescriptions: BLOCK_DESCRIPTIONS,
    sectionArticleType: SECTION_ARTICLE_TYPE,
    sectionsWithoutDocuments: SECTIONS_WITHOUT_DOCUMENTS,
    // Every section is opt-in, so a section that may carry nothing has no meaning
    // here: deselecting it is the way out.
    optionalSections: new Set(),
    orderedSectionKey: ORDERED_SECTION_KEY,
    nonMetadataKeys: NON_METADATA_KEYS,
    // Every section is opt-in, so the wizard opens on the section picker.
    fixedSections: null,
    // The config tables built from fields carrying a matching `group`. Mirrors
    // `tables` on the backend template, and for the same reason: a table this
    // template does not name can never reach its payload.
    payloadTables: ["sota"],
    emitsDeviceFamily: true,
    hasArticles: true,
    advisories,
};
